import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate, formatDay } from "utils/helpers";
import FileView from "components/fileView";
import DocumentViewerModal, { DocumentData } from "components/DocumentViewer";
import { LoadingIcon } from "utils/constant";
import ShowUI, {
  CanShow,
  useTeamRoles,
  LEGISLATIVE_DRAFTING_UI_RULES,
} from "utils/authRules";
import { useToast } from "contexts/ToastContext";
import {
  LAW_IMPLEMENTATION_REQUEST_STATUS,
  LEGAL_ADVISORY_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_STATUS,
} from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import CoordinatorRemark from "./coordinatorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import UploadEnforcementAnalysis from "./uploadEnforcementAnalysis";
import UploadDraftLaw from "./uploadDraftLaw";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import RespondBackForm from "pages/conductLegalResearch/fragments/respondBackform";
import LegislativeRespondBackForm from "./LegislativeResponback";
import SubmitAuditReport from "./submitAuditReport";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import TeamView from "components/team_view";
import TeamEditPopup from "./teamEditPopup";
import BroadcastCard from "pages/conductLegalResearch/fragments/broadcastCard";
import {
  createAuditLawImplementation,
  updateAuditLawImplementation,
  updateLawImplementationTargets,
} from "services/lawImplementationService";
import TopicList from "pages/conductLegalResearch/fragments/TopicList";
import TargetList from "./TargetList";

function AuditLawImplementationDetail({ request }: { request: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [publicFeedback, setPublicFeedback] = useState(false);
  const [uploadAmend, setUploadAmend] = useState(false);
  const [uploadEnforcementAnalysis, setUploadEnforcementAnalysis] = useState(false);
  const [auditDraft, setAuditDraft] = useState(false);
  const [showDraftHistory, setShowDraftHistory] = useState(false);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number | null>(null);
  const [viewTeam, setViewTeam] = useState(false);
  const [viewAuditTeam, setViewAuditTeam] = useState(false);
  const [showTeamEditPopup, setShowTeamEditPopup] = useState(false);
  const [edit, setEdit] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [formState, setFormState] = useState({ ...request });
  const contextData = useAuth();
  // Prosecutor actions go to the team leader only (TeamScope on the page).
  const actingRoles = useTeamRoles();
  const { showToast } = useToast();

  const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
  const [showSubmitReport, setShowSubmitReport] = useState(false);
  // For zonal audits the ZonalLIF user holds every approval role, so a single
  // approval should advance the whole phase instead of forwarding to the next reviewer.
  const zonalFastApprove =
    formState?.is_zonal_audit === true &&
    contextData?.user?.roles?.some((r: any) => r?.toLowerCase() === "zonallif");
  // For a zonal audit, a single approval on the enforcement-analysis proposal jumps
  // straight to the prosecutor (evidence + audit) — no coordinator/director/head chain.
  const isProposalCoordinator =
    formState?.is_zonal_audit === true &&
    formState?.current_approval_status ===
      LAW_IMPLEMENTATION_REQUEST_STATUS.proposal_submitted.value;
  const [enforcementDocViewerOpen, setEnforcementDocViewerOpen] = useState(false);
  const [enforcementDocuments, setEnforcementDocuments] = useState<DocumentData[]>([]);
  const [auditDocViewerOpen, setAuditDocViewerOpen] = useState(false);
  const [auditDocuments, setAuditDocuments] = useState<DocumentData[]>([]);
  const [evidenceDocViewerOpen, setEvidenceDocViewerOpen] = useState(false);
  const [evidenceDocuments, setEvidenceDocuments] = useState<DocumentData[]>([]);

  const enforcementHistoryDocs = React.useMemo(() => {
    const legacy = formState?.file_histories
      ?.filter((f: any) => f.file_type === "enforcement_analysis")
      .map((f: any) => ({
        uu_id: f.id?.toString() || "1",
        document_type: "enforcement_analysis",
        document_uuid: f.id?.toString() || "1",
        document_path: f.enforcement_analysis_file_details,
        created_at: f.created_at || new Date().toISOString(),
        updated_at: f.updated_at || new Date().toISOString(),
      })) || [];
    
    const documentFiles = formState?.documents
      ?.filter((d: any) => d.document_type === "enforcement_analysis_files")
      .map((d: any) => ({
        uu_id: d.uu_id,
        document_type: d.document_type,
        document_uuid: d.document_uuid,
        document_path: d.document_path,
        created_at: d.created_at,
        updated_at: d.updated_at,
      })) || [];
    
    const allDocs = [...legacy, ...documentFiles];
    return allDocs.sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [formState]);

  const auditFindingsDocs = React.useMemo(() => {
    const legacy = formState?.file_histories
      ?.filter((f: any) => f.file_type === "audit_findings")
      .map((f: any) => ({
        uu_id: f.id?.toString() || "1",
        document_type: "audit_findings",
        document_uuid: f.id?.toString() || "1",
        document_path: f.audit_findings_file_details,
        created_at: f.created_at || new Date().toISOString(),
        updated_at: f.updated_at || new Date().toISOString(),
      })) || [];
    
    const documentFiles = formState?.documents
      ?.filter((d: any) => d.document_type === "audit_findings_files")
      .map((d: any) => ({
        uu_id: d.uu_id,
        document_type: d.document_type,
        document_uuid: d.document_uuid,
        document_path: d.document_path,
        created_at: d.created_at,
        updated_at: d.updated_at,
      })) || [];
    
    const allDocs = [...legacy, ...documentFiles];
    return allDocs.sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [formState]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRespondBackClick = () => {
    setIsRespondBackFormOpen(!isRespondBackFormOpen);
  };

  const handleAdditionalInfoApprove = async () => {
    setLoading(true);
    const data = new FormData();

    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
    );

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegalResearchProposalApproved"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchProposalApproval"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = () => {};

  const handleIdentification = async () => {
    setLoading(true);
    const data = new FormData();
    data.append(
      "current_approval_status",
      LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value
    );
    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
    );

    try {
      const response = await updateAuditLawImplementation(formState.id, data);
      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("IdentificationCompleted"), "success");
    } catch (error) {
      console.error("Error updating request:", error);
      showToast(t("FailedToUpdateRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async (froward_status: string) => {
    const data = new FormData();
    if (
      formState.current_approval_status === "Under review" &&
      froward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }
    if (
      formState.current_approval_status === "Back to Head" &&
      froward_status ===
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }

    if (
      formState.current_approval_status === "Back to Director" &&
      froward_status ===
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
    ) {
      showToast(t("CannotForwardBeforeEditing"), "error");
      return;
    }

    data.append("forward_status", froward_status);

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAudit = async () => {
    const data = new FormData();
    data.append("audit_summary", formState.audit_summary);
    data.append("audit_summary_file", formState.audit_summary_file);
    data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.audit_submitted.value);

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
      setAuditDraft(false);
    }
  };

  const handleBroadcast = async (isPublished: boolean) => {
    setLoading(true);
    const data = new FormData();
    data.append("is_published", isPublished.toString());

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({
        ...prev,
        ...response,
        is_published: isPublished,
      }));
      showToast(isPublished ? t("LawImplementationPublished") : t("LawImplementationUnpublished"), "success");
    } catch (error) {
      console.error("Error updating broadcast status:", error);
      showToast(t("FailedToUpdateBroadcastStatus"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    const data = new FormData();
    data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.published.value);
    data.append(
      "forward_status",
      LEGAL_ADVISORY_REQUEST_STATUS.not_forwarded.value
    );

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
      setAuditDraft(false);
    }
  };

  const handleDraftApprove = async () => {
    const data = new FormData();
    if (formState.current_approval_status === "identification_in_progress") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
      );
    } else if (formState.current_approval_status === "proposal_submitted") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.audit_in_progress.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value
      );
    } else if (formState.current_approval_status === "audit_completed") {
      if (formState?.is_zonal_audit === true) {
        // Zonal audits are handled solely by the ZonalLIF, so the audit phase
        // completes straight to finalization (next work: registrar) — no management step.
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.finalization_complete.value
        );
        data.append(
          "forward_status",
          LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
        );
      } else {
        data.append(
          "current_approval_status",
          "audit_completed"
        );
        data.append(
          "forward_status",
          "forwarded_to_management"
        );
      }
    } else {
      data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.draft_approved.value);
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
      );
    }

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalApprove = async () => {
    const data = new FormData();
    if (formState.current_approval_status === "audit_completed") {
      data.append(
        "current_approval_status",
        LAW_IMPLEMENTATION_REQUEST_STATUS.finalization_complete.value
      );
      data.append(
        "forward_status",
        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
      );
    } else {
      data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.final_approval.value);
    }

    try {
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({ ...prev, ...response }));
      showToast(t("LegislativeDraftForwardedSuccess"), "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAcceptTargets = async () => {
    try {
      const targets = formState.identified_law_sectors || [];
      // For a zonal audit the ZonalLIF accepts targets directly (not at the
      // forwarded_to_head stage), so the sector/audit records must still be created.
      const isZonal =
        formState?.is_zonal_audit === true ||
        formState?.is_zonal === true ||
        contextData?.user?.roles?.some(
          (r: any) =>
            r?.toLowerCase() === "zonallif" || r?.toLowerCase() === "zonallrd"
        );
      const shouldCreateAudit = (target: any) =>
        target.is_slected_to_audit &&
        (formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
          isZonal);

      let internal_response: any;

      for (const target of targets) {
        if (shouldCreateAudit(target)) {
          const formData = new FormData();
          formData.append("requested_by", formState.requested_by);
          formData.append(
            "forward_status",
            LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_coordinator.value
          );
          formData.append(
            "current_approval_status",
            LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
          );
          formData.append("follow_up", formState.id);
          if (formState.application_number)
            formData.append("registry_number", formState.application_number);
          formData.append("assigned_coordinator_name", target.sector_name);
          // Mark the audit as zonal when the parent follow-up is zonal (or created by a zonal office).
          if (isZonal) formData.append("is_zonal_audit", "true");
          internal_response = await createAuditLawImplementation(formData);
        }
        const formData = new FormData();
        console.log(target);
        formData.append("sector_name", target.sector_name);
        formData.append(
          "is_slected_to_audit",
          String(target.is_slected_to_audit)
        );
        formData.append("follow_up", target.follow_up);
        if (shouldCreateAudit(target) && internal_response?.id)
          formData.append("audit_follow_up", internal_response.id);

        await updateLawImplementationTargets(target.id, formData);
      }

      const data = new FormData();

      if (
        formState.forward_status ===
          LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_head.value ||
        isZonal
      )
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.identification_complete.value
        );
      else
        data.append(
          "current_approval_status",
          LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value
        );
      const response = await updateAuditLawImplementation(formState.id, data);

      setFormState((prev: any) => ({
        ...prev,
        ...response,
      }));
      showToast(t("AcceptedTargets"), "success");
      setEdit(false);
      setSubmit(true);
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(t("FailedToUpdateLegalResearchRequest"), "error");
    }
  };

  useEffect(() => {
    console.log("formState: ", formState);
  }, [formState]);

  return (
    <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
      <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-3">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-semibold text-gray-800 ml-8">
              {t("RequestForLawImplementation")}
            </h1>
          </div>
          <div className="ml-auto flex items-center space-x-2">
            <StatusChip status={formState.current_approval_status} />
            <StatusChip status={formState.forward_status} />
          </div>

          {/* Registrar Forward to Coordinator */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.registrar_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="ml-auto flex gap-4">
              <button
                onClick={() =>
                  handleForward(
                    LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
                  )
                }
                type="button"
                className="bg-[#212121] text-white rounded-lg px-4"
              >
                {t("Forward")}
              </button>
            </div>
          </ShowUI>

          {/* Stakeholder Forward to Head */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div
                onClick={() =>
                  navigate("/legal-advisory/request", {
                    state: { request: formState },
                  })
                }
                className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <svg
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                    fill="black"
                  />
                </svg>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Head Forward to Prosecutor */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_prosecutor.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor.value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Head Forward to Coordinator */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_coordinator.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                        .value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Head Forward to Director */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <ShowUI
                ui_key={
                  LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director
                    .value
                }
                currentStatus={formState.current_approval_status}
                forwardStatus={formState.forward_status}
              >
                <div
                  onClick={() => setEdit(!edit)}
                  className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  {edit ? (
                    <p>{t("Cancel")}</p>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </div>
              </ShowUI>
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.under_review.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.not_forwarded.value
                        : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director
                            .value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Director Forward to Coordinator */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div
                onClick={() => setEdit(!edit)}
                className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                {edit ? (
                  <p>{t("Cancel")}</p>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                      fill="black"
                    />
                  </svg>
                )}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.back_to_head.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                        : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                            .value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Coordinator Forward to Prosecutor */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_prosecutor
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <ShowUI
                ui_key={
                  LEGISLATIVE_DRAFTING_UI_RULES
                    .edit_coordinator_forward_to_prosecutor.value
                }
                currentStatus={formState.current_approval_status}
                forwardStatus={formState.forward_status}
              >
                <div
                  onClick={() => setEdit(!edit)}
                  className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  {edit ? (
                    <p>{t("Cancel")}</p>
                  ) : (
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                </div>
              </ShowUI>
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      formState.current_approval_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.back_to_director.value
                        ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                            .value
                        : LEGAL_ADVISORY_REQUEST_STATUS.assigned_to_prosecutor
                            .value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Coordinator Forward to Head */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Additional Info Action */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_registrar.value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Prosecutor Forward to Coordinator */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    handleForward(
                      LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                        .value
                    )
                  }
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("Forward")}
                </button>
              </div>
            </div>
          </ShowUI>

          {/* Identification Complete Forward */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.identification_complete_forward
                .value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            {!(
              formState.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.identification_in_progress.value &&
              formState.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
            ) && (
            <div className="flex ml-auto gap-4">
              <div className="flex gap-4">
                <button
                  onClick={handleIdentification}
                  type="button"
                  className="bg-[#212121] text-white rounded-lg px-4"
                >
                  {t("IdentificationCompleted")}
                </button>
              </div>
            </div>
            )}
          </ShowUI>
        </div>

        {((contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
          formState?.current_approval_status === LAW_IMPLEMENTATION_REQUEST_STATUS.letter_refnum_submitted.value &&
          formState?.forward_status === LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value)) && (
          <BroadcastCard
            onClick={() => handleBroadcast(!formState.is_published)}
            isPublished={formState.is_published || false}
            title={t("BroadcastToPublicWebsite")}
            description={t("BroadcastLawImplementationDescription")}
            additionalInfo={t("BroadcastLawImplementationAdditionalInfo")}
          />
        )}

        {/* Publish Section */}
        <ShowUI
          ui_key={LEGISLATIVE_DRAFTING_UI_RULES.publish.value}
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <div className="flex mb-4">
            <button
              onClick={handlePublish}
              className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
            >
              {t("BroadcastToPublicWebsite")}
            </button>
          </div>
        </ShowUI>

        {/* Broadcasted Section */}
        <ShowUI
          ui_key={LEGISLATIVE_DRAFTING_UI_RULES.broadcasted.value}
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <div className="flex mb-4">
            <button
              className="p-4 flex-1 bg-[#212121] text-white rounded-lg"
              onClick={handlePublish}
            >
              {t("broadcasted")}
            </button>
          </div>
        </ShowUI>

        {/* Legal Request Advice File */}
        {formState?.legal_request_advice_file && (
          <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-400">
            <div className="pb-2 border-b border-gray-200 mb-4">
              <p>{t("SummaryWritten")}</p>
            </div>
            <p className="text-[#747171] font-normal text-xs mb-3">
              {formatDay(formState?.created_at).rest}
            </p>
            <p className="font-regular text-base text-[#606060]">
              {formState?.legal_request_advice_summary}
            </p>
            <div className="flex border border-gray-200 p-2 rounded-lg my-4">
              <FileView
                title={t("DraftLawFile")}
                file={formState?.legal_request_advice_file}
              />
            </div>
          </div>
        )}


        {/* Head Remark */}
        <ShowUI
          ui_key={
            LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value
          }
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <HeadRemark
            type="legal"
            setEdit={setEdit}
            setFormState={setFormState}
            formState={formState}
          />
        </ShowUI>

        {/* Director Remark */}
        <ShowUI
          ui_key={
            LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator
              .value
          }
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <DirectorRemark
            type="legal"
            setEdit={setEdit}
            setFormState={setFormState}
            formState={formState}
          />
        </ShowUI>

        {/* Selected target list — from the stored assigned_coordinator_name (above team assignment) */}
        {formState?.assigned_coordinator_name && (
          <div className="mt-2 mb-4 border border-gray-200 rounded-lg p-4">
            <p className="text-base font-bold text-[#000000] border-b border-gray-200 pb-2 mb-3">
              {t("IdentifiedLawSectors", { count: String(formState.assigned_coordinator_name).split(",").filter((s: string) => s.trim()).length }) || "Identified Law Sectors"}
            </p>
            <div className="space-y-2">
              {String(formState.assigned_coordinator_name)
                .split(",")
                .map((name: string) => name.trim())
                .filter((name: string) => name)
                .map((name: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <p className="text-sm font-semibold text-green-800">{name}</p>
                    <span className="ml-auto text-xs font-semibold text-green-700">✓ {t("Accepted") || "Accepted"}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Coordinator Remark */}
        <ShowUI
          ui_key={
            LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor
              .value
          }
          currentStatus={formState.current_approval_status}
          forwardStatus={formState.forward_status}
        >
          <CoordinatorRemark
            type="legal"
            setEdit={setEdit}
            setFormState={setFormState}
            formState={formState}
          />
        </ShowUI>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-scroll scrollbar-hide">
          {/* Prosecutor: Submit follow-up reports after the registrar reference number is submitted */}
          {(() => {
            const isProsecutor = actingRoles.includes("LegalResearchProsecutor");
            const canSubmitReport =
              formState?.current_approval_status ===
                LAW_IMPLEMENTATION_REQUEST_STATUS.letter_refnum_submitted.value &&
              isProsecutor;
            const reports =
              formState?.audit_followup_reports ||
              formState?.reports ||
              formState?.followup_reports ||
              formState?.audit_reports ||
              [];
            if (!canSubmitReport && reports.length === 0) return null;
            return (
              <div className="mt-6 mb-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                    {t("LawImplementationFollowUp") || "Law Implementation Follow-up"}
                  </p>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-amber-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-white text-base font-semibold">
                        {t("FollowUpReports") || "Follow-up Reports"}
                      </h3>
                      {reports.length > 0 && (
                        <span className="inline-flex items-center justify-center rounded-full bg-white/15 text-amber-200 text-xs font-semibold px-2 py-0.5">
                          {reports.length}
                        </span>
                      )}
                    </div>
                    {canSubmitReport && (
                      <button
                        type="button"
                        onClick={() => setShowSubmitReport(true)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5 transition-colors flex-shrink-0"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        {t("SubmitReports") || "Submit Reports"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  {reports.length === 0 ? (
                    <p className="text-sm text-gray-600">
                      {t("SubmitFollowUpReportsHint") ||
                        "The reference number has been registered. You can now submit one or more follow-up reports."}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {reports
                        .slice()
                        .reverse()
                        .map((report: any, idx: number) => (
                          <div
                            key={report.id || idx}
                            className="rounded-lg border border-gray-200 p-4"
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0A1D39] text-white text-xs font-bold">
                                  {reports.length - idx}
                                </span>
                                <span className="text-sm font-semibold text-gray-800">
                                  {report.given_by ||
                                    t("Report") ||
                                    "Report"}
                                </span>
                              </div>
                              {report.created_at && (
                                <span className="text-xs text-gray-400">
                                  {new Date(report.created_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {report.report_summary && (
                              <p className="text-sm text-gray-700 leading-relaxed text-justify mb-3">
                                {report.report_summary}
                              </p>
                            )}
                            {report.documents && report.documents.length > 0 && (
                              <DocumentViewerModal
                                documents={report.documents}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-followup-report-documents/document/`}
                              />
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Audit Execution Detail Section */}
          {(formState?.audit_findings || auditFindingsDocs.length > 0) && (() => {
            const canEditAudit = contextData?.user?.roles?.some(
              (role: any) =>
                role === "LegalResearchCoordinator" ||
                role === "OAGBManagementHead" ||
                role?.toLowerCase() === "zonallif"
            );
            return (
              <div className="mt-6 mb-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Governmental Header */}
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                        {t("AuditOutcome") || "Audit Outcome"}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <svg className="w-5 h-5 text-amber-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7l2 2 4-4" />
                        </svg>
                        <h3 className="text-white text-base font-semibold truncate">
                          {t("AuditExecutionDetail")}
                        </h3>
                      </div>
                    </div>
                    {canEditAudit && (
                      <button
                        type="button"
                        onClick={() => setAuditDraft(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5 transition-colors flex-shrink-0"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {t("Edit")}
                      </button>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="p-5">
                  {/* Findings summary */}
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 mb-4">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
                      {t("FindingsSummary") || "Findings Summary"}
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed text-justify">
                      {formState?.audit_findings || (t("NoFindings") || "—")}
                    </p>
                  </div>

                  {auditFindingsDocs.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t("NoAuditFindingsFile")}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                          {t("LatestVersion")}
                        </p>
                        {canEditAudit && (
                          <button
                            type="button"
                            onClick={() => setAuditDraft(true)}
                            className="text-xs font-semibold text-[#0A1D39] hover:underline"
                          >
                            {t("UpdateFile")}
                          </button>
                        )}
                      </div>
                      <div className="mb-4">
                        <DocumentViewerModal
                          documents={[auditFindingsDocs[0]]}
                          baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                        />
                      </div>
                      {auditFindingsDocs.length > 1 && (
                        <details className="border border-gray-200 rounded-lg">
                          <summary className="p-3 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-lg text-sm">
                            <span className="font-medium text-gray-700">
                              {t("PreviousVersions", { count: auditFindingsDocs.length - 1 })}
                            </span>
                          </summary>
                          <div className="p-3 space-y-3">
                            {auditFindingsDocs.slice(1).map((doc: any, index: number) => (
                              <div key={doc.document_uuid} className="border border-gray-200 rounded-lg p-2">
                                <p className="text-xs text-gray-500 mb-1">
                                  {t("VersionNumber", { version: auditFindingsDocs.length - 1 - index, date: new Date(doc.created_at).toLocaleDateString() })}
                                </p>
                                <DocumentViewerModal
                                  documents={[doc]}
                                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                                  hideEmptyFile={true}
                                />
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Final Approval Section for Management */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.final_approval.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={LEGAL_ADVISORY_REQUEST_STATUS.audit_responded.value}
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                onClick={handleRespondBackClick}
                disabled={loading}
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleForward("forwarded_to_management")}
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {t("ApproveAndForwardToManagement") || "Approve and Forward to Management"}
                  </>
                )}
              </button>
            </div>
          </ShowUI>

          {/* Forward Draft Section */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={
                LAW_IMPLEMENTATION_REQUEST_STATUS.proposal_responded.value
              }
              type="legal"
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                onClick={handleRespondBackClick}
                disabled={loading}
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() =>
                  handleForward(
                    LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director.value
                  )
                }
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {t("ApproveAndForwardToDirector") || "Approve and Forward to Director"}
                  </>
                )}
              </button>
            </div>
          </ShowUI>

          {/* Forward Draft to Director */}
          <ShowUI
            ui_key={
              LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value
            }
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={
                LAW_IMPLEMENTATION_REQUEST_STATUS.proposal_responded.value
              }
              type="legal"
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                onClick={handleRespondBackClick}
                disabled={loading}
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() =>
                  zonalFastApprove || isProposalCoordinator
                    ? handleDraftApprove()
                    : handleForward(
                        formState.forward_status ===
                          LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator
                            .value
                          ? LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_director
                              .value
                          : LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_head.value
                      )
                }
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {isProposalCoordinator
                      ? t("ApproveAndForwardToProsecutor") ||
                        "Approve and Forward to Prosecutor"
                      : zonalFastApprove
                      ? t("ApproveAndContinue") || "Approve & Continue"
                      : formState.forward_status ===
                        LEGAL_ADVISORY_REQUEST_STATUS.forwarded_to_coordinator.value
                      ? t("ApproveAndForwardToDirector") ||
                        "Approve and Forward to Director"
                      : t("ApproveAndForwardToHead") ||
                        "Approve and Forward to Head"}
                  </>
                )}
              </button>
            </div>
          </ShowUI>

          {/* Forward Draft to Head */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={
                LAW_IMPLEMENTATION_REQUEST_STATUS.proposal_responded.value
              }
              type="legal"
            />

            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                onClick={handleRespondBackClick}
                disabled={loading}
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleDraftApprove}
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {isProposalCoordinator
                      ? t("ApproveAndForwardToProsecutor") ||
                        "Approve and Forward to Prosecutor"
                      : formState.current_approval_status === "audit_completed"
                      ? t("ApproveAndForwardToManagement") ||
                        "Approve and Forward to Management"
                      : t("Approve")}
                  </>
                )}
              </button>
            </div>
          </ShowUI>

          {/* Management Head Final Approval Section */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.oagb_management_head_approve_respond.value}
            currentStatus={formState.current_approval_status}
            forwardStatus={formState.forward_status}
          >
            <LegislativeRespondBackForm
              request={formState}
              setLegalResearch={setFormState}
              isOpen={isRespondBackFormOpen}
              onClose={() => setIsRespondBackFormOpen(false)}
              forward={LEGAL_ADVISORY_REQUEST_STATUS.audit_responded.value}
            />
            <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="button"
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                onClick={handleRespondBackClick}
                disabled={loading}
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2m-15-7l4-4m-4 4l4 4" />
                    </svg>
                    {t("RespondBack")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleFinalApprove}
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-[#0A1D39]/40 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>{LoadingIcon} {t("Submitting")}</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {t("FinalApproveAuditFollowup") || "Final Approve Audit Follow-up"}
                  </>
                )}
              </button>
            </div>
          </ShowUI>

          {/* Evidence Section */}
          {formState?.audit_evidences?.length > 0 && (() => {
            const evidenceList = formState?.audit_evidences || [];
            const activeIndex =
              selectedEvidenceIndex !== null
                ? selectedEvidenceIndex
                : evidenceList.length - 1;
            const activeEvidence = evidenceList[activeIndex];
            return (
              <div className="mt-6 mb-8 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Governmental Header */}
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                        {t("AuditEvidenceRecord") || "Audit Evidence Record"}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <svg className="w-5 h-5 text-amber-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-white text-base font-semibold truncate">
                          {t("Evidence") || "Evidence"}
                        </h3>
                        <span className="inline-flex items-center justify-center rounded-full bg-white/15 text-amber-200 text-xs font-semibold px-2 py-0.5">
                          {evidenceList.length}
                        </span>
                      </div>
                    </div>
                    {evidenceList.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setShowDraftHistory(!showDraftHistory)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5 transition-colors flex-shrink-0"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        {showDraftHistory
                          ? t("HideList") || "Hide List"
                          : t("ShowEvidences") || "View All"}
                      </button>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="p-5">
                  {showDraftHistory ? (
                    <div className="space-y-3">
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">
                        {t("AllEvidence") || "All Evidence"}
                      </p>
                      {evidenceList.map((evidence: any, idx: number) => {
                        const evidenceIndex = evidenceList.length - 1 - idx;
                        const isSelected = activeIndex === evidenceIndex;
                        const isLatest = idx === 0;
                        return (
                          <button
                            type="button"
                            key={idx}
                            className={`w-full text-left border rounded-lg p-3 transition-colors ${
                              isSelected
                                ? "border-[#0A1D39] bg-[#0A1D39]/5 ring-1 ring-[#0A1D39]/20"
                                : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                            }`}
                            onClick={() => {
                              setSelectedEvidenceIndex(evidenceIndex);
                              setShowDraftHistory(false);
                            }}
                          >
                            <div className="flex justify-between items-start mb-1 gap-2">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0A1D39] text-white text-xs font-bold flex-shrink-0">
                                  {evidenceList.length - idx}
                                </span>
                                <p className="text-sm font-semibold text-gray-800">
                                  {t("EvidenceNumber", { number: evidenceList.length - idx }) || `Evidence ${evidenceList.length - idx}`}
                                </p>
                                {isLatest && (
                                  <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                    {t("Latest") || "Latest"}
                                  </span>
                                )}
                              </div>
                              {evidence.uploaded_at && (
                                <p className="text-xs text-gray-400 flex-shrink-0">
                                  {new Date(evidence.uploaded_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2 pl-8">
                              {evidence.summary_of_evidence || (t("NoSummary") || "No summary provided")}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2 pl-8">
                              {evidence.documents && evidence.documents.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-[#0A1D39] bg-gray-100 rounded-full px-2 py-0.5">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  {t("FileCount", { count: evidence.documents.length }) || `${evidence.documents.length} files`}
                                </span>
                              )}
                              {evidence.collectors && evidence.collectors.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" />
                                  </svg>
                                  {t("CollectorCount", { count: evidence.collectors.length }) || `${evidence.collectors.length} collectors`}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <>
                      {/* Active evidence meta */}
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0A1D39] text-white text-xs font-bold">
                            {activeIndex + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-800">
                            {t("EvidenceNumber", { number: activeIndex + 1 }) || `Evidence ${activeIndex + 1}`}
                          </span>
                          {activeIndex === evidenceList.length - 1 && (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                              {t("Latest") || "Latest"}
                            </span>
                          )}
                        </div>
                        {activeEvidence?.uploaded_at && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(activeEvidence.uploaded_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* Summary */}
                      <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 mb-4">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
                          {t("SummaryOfEvidence") || "Summary of Evidence"}
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed text-justify">
                          {activeEvidence?.summary_of_evidence || (t("NoSummary") || "No summary provided")}
                        </p>
                      </div>

                      {/* Attached files */}
                      {activeEvidence?.documents && activeEvidence.documents.length > 0 && (
                        <div className="mb-4">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
                            {t("AttachedFiles") || "Attached Files"}
                          </p>
                          <DocumentViewerModal
                            documents={activeEvidence.documents}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/law-follow-up-evidence-documents/document/`}
                          />
                        </div>
                      )}

                      {/* Collectors */}
                      {activeEvidence?.collectors && activeEvidence.collectors.length !== 0 && (
                        <div className="flex bg-gray-50 rounded-lg border border-gray-200">
                          <ActionAvatars
                            title={t("EvidenceCollectors")}
                            users={activeEvidence.collectors.map((c: any) => ({ full_name: c.name }))}
                            prosecutor={true}
                          />
                          <svg
                            onClick={() => setViewTeam(!viewTeam)}
                            className="mr-2 ml-auto my-auto cursor-pointer"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <mask
                              id="mask0_9_31087"
                              maskUnits="userSpaceOnUse"
                              x="1"
                              y="5"
                              width="22"
                              height="14"
                            >
                              <path
                                d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                                fill="white"
                                stroke="white"
                                strokeWidth="1.33333"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                                fill="black"
                                stroke="black"
                                strokeWidth="1.33333"
                                strokeLinejoin="round"
                              />
                            </mask>
                            <g mask="url(#mask0_9_31087)">
                              <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                            </g>
                          </svg>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Draft and Additional File Section — only the assigned prosecutor submits evidence/audit/law */}
          {actingRoles.includes("LegalResearchProsecutor") && (
            <ShowUI
              ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
              currentStatus={formState.current_approval_status}
              forwardStatus={formState.forward_status}
            >
              <ProsecutorRemark setFormState={setFormState} formState={formState} />
            </ShowUI>
          )}

          {/* Documents Section */}
          <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
            {formState?.request_letter_file && (
              <div className="flex items-center justify-between ">
                <FileView
                  title={t("RequestLetter")}
                  file={formState?.request_letter_file}
                />
              </div>
            )}

            <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-5 h-5 text-[#0A1D39]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{t("Law") || "Law"}</p>
              </div>
              <p className="text-base font-semibold text-[#0A1D39]">
                {formState?.identifies_specific_laws_government_sectors || "—"}
              </p>
              {formState?.description && (
                <p className="text-sm text-gray-500 mt-2 leading-relaxed text-justify">
                  {formState.description}
                </p>
              )}
            </div>

            {(formState?.audit_teams?.length > 0 || formState?.teams?.length > 0) && (
              <div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                <ActionAvatars
                  title={t("TeamList")}
                  users={(formState?.audit_teams || formState?.teams || []).filter(
                    (team: any) => team.team_type === "audit" || team.team_type === "prosecutor"
                  )}
                  prosecutor={true}
                />
                {(contextData?.user?.roles?.some(
                  (p) => p === "LegalResearchCoordinator" || p === "LRDIFUDDirector" || p?.toLowerCase() === "zonallif"
                )) && (
                    <svg
                      onClick={() => setShowTeamEditPopup(true)}
                      className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z"
                        fill="black"
                      />
                    </svg>
                  )}
                <svg
                  onClick={() => setViewAuditTeam(!viewAuditTeam)}
                  className="mr-2 ml-auto my-auto cursor-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <mask
                    id="mask0_9_31087"
                    maskUnits="userSpaceOnUse"
                    x="1"
                    y="5"
                    width="22"
                    height="14"
                  >
                    <path
                      d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z"
                      fill="white"
                      stroke="white"
                      strokeWidth="1.33333"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z"
                      fill="black"
                      stroke="black"
                      strokeWidth="1.33333"
                      strokeLinejoin="round"
                    />
                  </mask>
                  <g mask="url(#mask0_9_31087)">
                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                  </g>
                </svg>
              </div>
            )}
          </div>

          {/* Enforcement Analysis Section */}
          {formState?.enforcement_analysis_summary && (
            <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
              {formState?.registry_number && (
                <p className="text-[#432101AB] font-normal text-base">
                  {t("RegisterNumber")}: {formState.registry_number}
                </p>
              )}
              {auditFindingsDocs.length > 0 && (
                <div className="mb-4">
                  <p className="text-[#432101AB] font-normal text-base mb-2">{t("AuditFindingsFiles")}:</p>
                  <DocumentViewerModal
                    documents={auditFindingsDocs}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                  />
                </div>
              )}
              <div className="flex gap-2">
                <p className="text-[#432101AB] font-normal text-base">
                  {t("EnforcementAnalysis")}
                </p>
                {contextData?.user?.roles?.some(
                  (role: any) =>
                    role === "LegalResearchCoordinator" ||
                    role === "OAGBManagementHead"
                ) || contextData?.user?.roles?.some((role: any) => role?.toLowerCase() === "zonallif") && (
                  <button
                    onClick={() => setUploadEnforcementAnalysis(true)}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                  >
                    {t("Edit")}
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-500 font-medium mb-0">
                  {formState?.enforcement_analysis_summary}
                </p>
              </div>
              
              {enforcementHistoryDocs.length === 0 ? (
                <p className="text-gray-500">{t("NoEnforcementAnalysisFile")}</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">{t("LatestVersion")}</p>
                    {contextData?.user?.roles?.some(
                      (role: any) =>
                        role === "LegalResearchCoordinator" ||
                        role === "OAGBManagementHead"
                    ) || contextData?.user?.roles?.some((role: any) => role?.toLowerCase() === "zonallif") && (
                      <button
                        onClick={() => setUploadEnforcementAnalysis(true)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium px-2 py-1 rounded border border-blue-300 hover:bg-blue-50"
                      >
                        {t("UpdateFile")}
                      </button>
                    )}
                  </div>
                  <div className="mb-4">
                    <DocumentViewerModal
                      documents={[enforcementHistoryDocs[0]]}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                    />
                  </div>
                  {enforcementHistoryDocs.length > 1 && (
                    <div>
                      <details className="border border-gray-300 rounded-lg">
                        <summary className="p-3 cursor-pointer bg-gray-50 hover:bg-gray-100 rounded-lg">
                          <span className="font-medium">
                            {t("PreviousVersions", { count: enforcementHistoryDocs.length - 1 })}
                          </span>
                        </summary>
                        <div className="p-3 space-y-3">
                          {enforcementHistoryDocs.slice(1).map((doc: any, index: number) => (
                            <div key={doc.document_uuid} className="border border-gray-200 rounded-lg p-2">
                              <p className="text-xs text-gray-500 mb-1">
                                {t("VersionNumber", { version: enforcementHistoryDocs.length - 1 - index, date: new Date(doc.created_at).toLocaleDateString() })}
                              </p>
                              <DocumentViewerModal
                                documents={[doc]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                                hideEmptyFile={true}
                              />
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </>
              )}
            </div>
          )}




          {/* Remark Section */}
          <ShowUI
            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
            currentStatus={"all"}
            forwardStatus={"all"}
          >
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "head"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForDirector")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "head")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "director"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForCoordinator")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "director")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
            {formState.remarks &&
              formState?.remarks?.find(
                (remark: any) => remark.role === "coordinator"
              ) && (
                <div>
                  <p className="text-[#432101AB] font-normal text-base">
                    {t("RemarkForProsecutor")}
                  </p>
                  <p className="text-gray-500 font-medium mb-2">
                    {
                      formState?.remarks
                        .filter((remark: any) => remark.role === "coordinator")
                        .slice()
                        .reverse()[0]?.remark
                    }
                  </p>
                  <br />
                </div>
              )}
          </ShowUI>
        </div>
      </div>

      {/* Modals */}
      {showSubmitReport && (
        <SubmitAuditReport
          request={formState}
          setLegalResearch={setFormState}
          isOpen={showSubmitReport}
          onClose={() => setShowSubmitReport(false)}
        />
      )}
      {publicFeedback && (
        <PublicFeedback
          data={formState}
          setLegalResearch={setFormState}
          isOpen={publicFeedback}
          onClose={() => setPublicFeedback(false)}
        />
      )}
      {uploadAmend && (
        <UploadAmendedFile
          data={formState}
          setLegalResearch={setFormState}
          isOpen={uploadAmend}
          onClose={() => setUploadAmend(false)}
        />
      )}
                {uploadEnforcementAnalysis && (
            <UploadEnforcementAnalysis
              data={formState}
              setLegalResearch={setFormState}
              isOpen={uploadEnforcementAnalysis}
              onClose={() => setUploadEnforcementAnalysis(false)}
            />
          )}
          {auditDraft && (
            <UploadDraftLaw
              request={formState}
              setLegalResearch={setFormState}
              isOpen={auditDraft}
              onClose={() => setAuditDraft(false)}
              type="legal"
            />
          )}
          {viewTeam && (
            <TeamView
              teamMembers={[
                ...(formState?.audit_evidences?.[selectedEvidenceIndex !== null ? selectedEvidenceIndex : formState?.audit_evidences?.length - 1]?.collectors || [])
              ]}
              onClose={() => setViewTeam(false)}
            />
          )}
          {viewAuditTeam && (
            <TeamView
              teamMembers={(formState?.audit_teams || formState?.teams || []).filter(
                (team: any) => team.team_type === "audit" || team.team_type === "prosecutor"
              )}
              onClose={() => setViewAuditTeam(false)}
            />
          )}
          {showTeamEditPopup && (
            <TeamEditPopup
              teamData={{
                audit_folloup: formState.id,
                team_type: "audit",
                prosecutors: (formState?.audit_teams || []).filter(
                  (team: any) => team.team_type === "audit" || team.team_type === "prosecutor"
                ),
              }}
              onClose={() => setShowTeamEditPopup(false)}
              onSuccess={() => {
                setShowTeamEditPopup(false);
                showToast(t("TeamUpdatedSuccess") || "Team updated successfully", "success");
              }}
            />
          )}
          {evidenceDocViewerOpen && (
            <DocumentViewerModal
              label=""
              documents={evidenceDocuments}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/law-follow-up-evidence-documents/document/`}
              hide={!evidenceDocViewerOpen}
              showMetadata={true}
              layout="list"
            />
          )}
          {auditDocViewerOpen && (
            <DocumentViewerModal
              label={t("AuditDocuments")}
              documents={auditDocuments}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
              hide={!auditDocViewerOpen}
              showMetadata={true}
              layout="list"
            />
          )}
          {enforcementDocViewerOpen && (
            <DocumentViewerModal
              label={t("EnforcementAnalysis")}
              documents={enforcementDocuments}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
              hide={!enforcementDocViewerOpen}
              showMetadata={true}
              layout="list"
            />
          )}
        </div>
      );
    }

    export default AuditLawImplementationDetail;